// Phones and tablets. The game is built around a mouse: right-click for a close-up or a menu, the wheel to
// zoom, hover for names. On a touch screen:
//   - press and hold (a long-press) does what a right-click does, everywhere in the game
//   - the table and the card close-ups pinch to zoom (TableView and ui.ts handle their own two-finger moves)

/** Is the main way of pointing here a finger? */
export function isTouchScreen() {
  return typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
}

const HOLD_MS = 480; // how long a finger rests before it counts as a long-press
const SLOP = 10; // px the finger may wander and still be "resting"

let installed = false;

/**
 * Turns a long-press with one finger into a right-click (a `contextmenu` event at the spot), for every part of
 * the game. Whatever the finger started (a drag, a pending click) is told it was cancelled first (`pointercancel`),
 * and the finger's lift (`pointerup` / `click`) that ends the long-press is swallowed, so it does nothing else.
 */
export function installLongPress() {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  let press: { id: number; x: number; y: number; target: Element; timer: number } | null = null;
  let fired: number | null = null; // pointerId whose long-press has fired: its lift is swallowed
  let fingers = 0;
  let quietUntil = 0; // the browser's own contextmenu for a touch is ignored until then (ours replaces it)

  const stop = () => {
    if (press) clearTimeout(press.timer);
    press = null;
  };
  const skip = (t: Element) => !!t.closest('input, textarea, select, [contenteditable="true"], .no-long-press');

  window.addEventListener(
    'pointerdown',
    (e) => {
      if (e.pointerType !== 'touch') return;
      fingers++;
      quietUntil = Date.now() + 1500;
      if (fingers > 1 || !e.isPrimary) {
        stop(); // two fingers: a pinch, never a long-press
        return;
      }
      const target = e.target as Element | null;
      if (!target || skip(target)) return;
      stop();
      const id = e.pointerId;
      const x = e.clientX;
      const y = e.clientY;
      press = {
        id, x, y, target,
        timer: window.setTimeout(() => {
          if (!press || press.id !== id) return;
          press = null;
          fired = id;
          quietUntil = Date.now() + 1500;
          target.dispatchEvent(new PointerEvent('pointercancel', { bubbles: true, pointerId: id, pointerType: 'touch', isPrimary: true, clientX: x, clientY: y }));
          target.dispatchEvent(new MouseEvent('contextmenu', { bubbles: true, cancelable: true, clientX: x, clientY: y, button: 2, buttons: 0 }));
          navigator.vibrate?.(12);
        }, HOLD_MS),
      };
    },
    true,
  );
  window.addEventListener(
    'pointermove',
    (e) => {
      if (press && e.pointerId === press.id && Math.hypot(e.clientX - press.x, e.clientY - press.y) > SLOP) stop();
      if (fired === e.pointerId) e.stopImmediatePropagation(); // (nothing follows the finger after a long-press)
    },
    true,
  );
  const lift = (e: PointerEvent) => {
    if (e.pointerType !== 'touch') return;
    fingers = Math.max(0, fingers - 1);
    if (press?.id === e.pointerId) stop();
    if (fired === e.pointerId && e.isTrusted) {
      fired = null;
      e.stopImmediatePropagation();
      // and the click the browser sends after this lift
      const eat = (c: Event) => { c.stopImmediatePropagation(); c.preventDefault(); };
      window.addEventListener('click', eat, { capture: true, once: true });
      setTimeout(() => window.removeEventListener('click', eat, true), 400);
    }
  };
  window.addEventListener('pointerup', lift, true);
  window.addEventListener('pointercancel', (e) => { if (e.isTrusted) lift(e); }, true);
  // Android sends its own contextmenu for a long-press: ours has already gone (or is about to), so it is dropped
  window.addEventListener(
    'contextmenu',
    (e) => {
      if (!e.isTrusted || Date.now() > quietUntil) return;
      const t = e.target as Element | null;
      if (t && skip(t)) return;
      e.preventDefault();
      e.stopImmediatePropagation();
    },
    true,
  );
}

/**
 * Two-finger pinch on an element: `onPinch(scale, midX, midY, dx, dy)` with the change since the last move
 * (scale > 1 = fingers apart) and how far the midpoint between the fingers moved. Returns a function that says
 * whether a pinch is happening (so one-finger handlers can stand aside).
 */
export function pinchable(target: HTMLElement, onPinch: (scale: number, midX: number, midY: number, dx: number, dy: number) => void, onStart?: () => void) {
  const pts = new Map<number, { x: number; y: number }>();
  let last: { d: number; mx: number; my: number } | null = null;
  const measure = () => {
    const [a, b] = [...pts.values()];
    return { d: Math.hypot(a.x - b.x, a.y - b.y) || 1, mx: (a.x + b.x) / 2, my: (a.y + b.y) / 2 };
  };
  target.addEventListener('pointerdown', (e) => {
    if (e.pointerType !== 'touch') return;
    pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pts.size === 2) {
      last = measure();
      onStart?.();
    }
  });
  target.addEventListener('pointermove', (e) => {
    const p = pts.get(e.pointerId);
    if (!p) return;
    p.x = e.clientX;
    p.y = e.clientY;
    if (pts.size !== 2 || !last) return;
    const now = measure();
    onPinch(now.d / last.d, now.mx, now.my, now.mx - last.mx, now.my - last.my);
    last = now;
  });
  const end = (e: PointerEvent) => {
    pts.delete(e.pointerId);
    if (pts.size < 2) last = null;
  };
  target.addEventListener('pointerup', end);
  target.addEventListener('pointercancel', (e) => { if (e.isTrusted) end(e); });
  target.addEventListener('pointerleave', (e) => { if (e.pointerType === 'touch') end(e); });
  return () => last !== null || pts.size >= 2;
}
